import styled from "@emotion/styled";
import {
  Stack,
  Container,
  H1,
  FlexRow,
  Button,
  IconTwitter,
  IconInstagram,
  Link,
  Text,
  IconWebsite,
  IconArrow,
  H2,
  RichText,
} from "atoms";
import { GenericImage } from "features/images";
import { Head } from "features/misc";
import type { Collective } from "../types";

type CollectiveHeaderProps = {
  collective: Collective;
  artistCount?: number;
  handleFollowAll?: VoidFunction;
  isCollectiveUpdatesPage?: boolean;
};

function CollectiveHeader({
  artistCount,
  handleFollowAll,
  isCollectiveUpdatesPage = false,
  collective: {
    id,
    badge,
    banner,
    name,
    description,
    links,
    hasCollectivePage,
  },
}: CollectiveHeaderProps) {
  return (
    <CenteredHeader>
      <Head
        title={`${name} on HUG`}
        description={description}
        socialTitle={`Check out ${name} on HUG!`}
        socialDescription={description}
      />
      {hasCollectivePage && <Banner src={banner} sizes="100vw" />}

      <Container width="sm">
        <Stack gap="sm">
          <CollectiveLogo isCollective={hasCollectivePage}>
            <GenericImage src={badge} />
          </CollectiveLogo>

          <Stack gap="0">
            {hasCollectivePage && (
              <Link variant="blank" to="/collectives" size="xxs">
                <IconArrow dir="l" /> Collectives
              </Link>
            )}
            <H1 size="h2">{name}</H1>
            {!hasCollectivePage && (
              <Text size="xs" bold>
                {artistCount} Artists
              </Text>
            )}
          </Stack>

          {isCollectiveUpdatesPage ? (
            <Stack gap="sm">
              <H2 size="lg" textAlign="center">
                Recent Posts from our Artists
              </H2>

              <Text size="xs" bold textAlign="center">
                <Link variant="blank" to={`/collectives/${id}`}>
                  <IconArrow dir="l" /> See {name} Overview
                </Link>
              </Text>
            </Stack>
          ) : (
            <>
              {hasCollectivePage && (
                <FlexRow justifyContent="center" gap="10px">
                  <Link
                    to={`/collectives/${id}#artists`}
                    size="xs"
                    variant="secondary"
                  >
                    View All {artistCount} Artists
                  </Link>
                  {handleFollowAll && (
                    <Button onClick={handleFollowAll} size="xs">
                      Follow All Artists in {name}
                    </Button>
                  )}
                </FlexRow>
              )}

              <RichText value={description} />

              {links.length > 0 && (
                <FlexRow justifyContent="center">
                  {links.map(({ type, url }) => {
                    if (url === "") return null;
                    return (
                      <CircleLink
                        key={url}
                        href={url}
                        target="_blank"
                        aria-label={`Visit ${name}'s {type}`}
                      >
                        {type === "twitter" && <IconTwitter aria-hidden />}
                        {type === "instagram" && <IconInstagram aria-hidden />}
                        {type !== "twitter" && type !== "instagram" && (
                          <IconWebsite aria-hidden />
                        )}
                      </CircleLink>
                    );
                  })}
                </FlexRow>
              )}
            </>
          )}
        </Stack>
      </Container>
    </CenteredHeader>
  );
}

const CenteredHeader = styled.div({
  textAlign: "center",
  marginBottom: "3rem",
});

const Banner = styled(GenericImage)(({ theme }) => ({
  aspectRatio: "auto",
  width: "100%",
  height: 200,
  marginBottom: -150,
  overflow: "hidden",
  img: {
    background: theme.colors.bg,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
}));

const CollectiveLogo = styled.div<{ isCollective: boolean }>(
  ({ isCollective, theme }) => ({
    backgroundColor: theme.colors.bg,
    boxShadow: isCollective ? theme.boxShadow.light : "none",
    border: isCollective ? "5px solid" : 0,
    borderColor: theme.colors.bg,
    borderRadius: isCollective ? theme.borderRadius.sm : 0,
    width: isCollective ? 200 : 150,
    margin: "0 auto",
    paddingTop: isCollective ? 0 : "2rem",
    position: "relative",
    zIndex: 2,
    [theme.breakpoints.xs]: {
      borderWidth: isCollective ? 7 : 0,
    },
    [theme.breakpoints.desktop]: {
      borderWidth: isCollective ? 10 : 0,
    },
    img: { background: theme.colors.bg },
  })
);

const CircleLink = styled(Link)(({ theme }) => ({
  borderRadius: theme.borderRadius.round,
  border: "2px solid currentColor",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: 36,
  height: 36,
}));

export { CollectiveHeader };
